/**
 * The business unit the operator is currently looking at.
 *
 * Every view is scoped to exactly ONE brand. There is no "all business units": an
 * unscoped view could not be written to safely once every row carries a brand — a write
 * with no brand matches only unbranded rows, and after the backfill there are none, so it
 * either 404s or silently creates a second, shared row beside the brand's own.
 *
 * `null` here means ONLY "the brand list has not loaded yet". `useBusinessUnit` resolves
 * it to the account's default brand as soon as the list arrives, and writes that back.
 * A selection scopes every entity view in the product EXCEPT the Users page, which always
 * lists everyone on the account.
 *
 * Persisted per browser so a scoped view survives a refresh. Deliberately NOT mirrored
 * into the URL: a pasted id this account does not own would scope every list to nothing.
 *
 * The id is a STRING, not a number: machine's business_units.id is a BIGINT and the pg
 * driver hands it back as a string. Comparing it as a number is a silent miss, so it stays
 * a string end to end.
 */

import { atomWithStorage } from 'jotai/utils';

/** Registered in ACCOUNT_SCOPED_KEYS — see the note in src/lib/account-storage.ts. */
export const SELECTED_BUSINESS_UNIT_KEY = 'selected-business-unit-kim-ai';

// `getOnInit` is false by default in jotai 2, which means the first render reads `null`
// even when a brand is stored: every scoped query fires once as "all", the stored id then
// arrives, and they all fire a second time. That is a visible double fetch AND a flash of
// other brands' rows. Reading the stored value during init costs one synchronous
// localStorage read and removes both.
export const selectedBusinessUnitIdAtom = atomWithStorage<string | null>(SELECTED_BUSINESS_UNIT_KEY, null, undefined, {
  getOnInit: true
});
